import { Fragment, useCallback } from 'react';

import { Icon } from '@iconify/react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { MarkdownViewer } from '@core/components/ui';
import { formatTime } from '@core/i18n/format';
import { useAppSelector } from '@core/store/hooks';
import { parseCitationMarkers, stripCitationSeparators } from '@core/utils/citations';

import { selectCurrentProject } from '@features/projects';

import { ThinkingTimeline } from './ThinkingTimeline';
import { UploadLinkWidget } from './UploadLinkWidget';
import { UserQuestionReadOnly } from './UserQuestionReadOnly';

/**
 * Resolve the external URL for a citation.
 *
 * Handles both the canonical `url` field and legacy data where the URL
 * was stored in `document_uuid`.
 */
function getCitationUrl(citation) {
  if (citation.url) return citation.url;
  if (citation.document_uuid?.startsWith('http')) return citation.document_uuid;
  return null;
}

/** Build /viewer/ path for a local document citation, including ?type= param. */
function buildViewerHref(projectUuid, citation) {
  if (!projectUuid || !citation.document_uuid) return null;
  const name = citation.document_name || '';
  const dotIdx = name.lastIndexOf('.');
  const ext = dotIdx > 0 ? name.slice(dotIdx) : '';
  return `/viewer/${projectUuid}/${citation.document_uuid}${ext ? `?type=${encodeURIComponent(ext)}` : ''}`;
}

/**
 * Clean up LLM-generated citation names.
 *
 * For web citations with a URL, show the hostname; strip tool-name prefixes.
 */
function formatCitationName(citation, t) {
  const url = getCitationUrl(citation);
  if (url) {
    try {
      return new URL(url).hostname.replace(/^www\./, '');
    } catch {
      // fall through
    }
  }
  return (citation.document_name || t('ai.chatMessage.unknownSource'))
    .replace(/^search_web result\s*\((.+)\)$/i, '$1')
    .replace(/^fetch_web_page result\s*\((.+)\)$/i, '$1');
}

/**
 * Replace [N] markers in a text string with clickable citation superscripts.
 *
 * Returns an array of React nodes safe to spread as children.
 */
function processInlineCitations(children, citations, onCitationClick) {
  if (!children) return children;
  const childArray = Array.isArray(children) ? children : [children];
  return childArray.flatMap((child, ci) => {
    if (typeof child !== 'string') return [child];

    const segments = stripCitationSeparators(parseCitationMarkers(child));
    if (segments.length === 1 && segments[0].type === 'text') return [child];

    return segments.flatMap((segment, si) => {
      const key = `${ci}-${si}`;
      if (segment.type === 'text') return [<Fragment key={key}>{segment.content}</Fragment>];

      return segment.numbers.map((n) => {
        const citation = citations?.[n - 1];
        if (!citation) return <Fragment key={`${key}-${n}`}>{`[${n}]`}</Fragment>;
        return (
          <sup key={`${key}-${n}`}>
            <button
              onClick={() => onCitationClick(citation)}
              className="inline-flex items-center justify-center size-4 text-xs font-bold text-white bg-purple-500 rounded-full hover:bg-purple-700 cursor-pointer transition-colors ml-0.5 leading-none"
              title={citation.document_name}
            >
              {n}
            </button>
          </sup>
        );
      });
    });
  });
}

/**
 * ChatMessage component for rendering user and assistant messages
 */
export function ChatMessage({ message, agentName = 'Neovision Platform', onUploadComplete }) {
  const { t, i18n } = useTranslation();
  const currentProject = useAppSelector(selectCurrentProject);
  const isUser = message.role === 'user';
  const isLoading = message.isLoading;
  const projectUuid = message.project_uuid || currentProject?.uuid;

  const handleCitationClick = useCallback(
    (citation) => {
      const webUrl = getCitationUrl(citation);
      if (webUrl) {
        window.open(webUrl, '_blank', 'noopener,noreferrer');
      } else {
        const viewerHref = buildViewerHref(projectUuid, citation);
        if (viewerHref) window.open(viewerHref, '_blank');
      }
    },
    [projectUuid],
  );

  // Qori-style loading state with pulsing lightbulb
  if (isLoading) {
    return (
      <div className="flex gap-3 py-4">
        <div className="flex-shrink-0">
          <div className="flex items-center justify-center size-8 rounded-full bg-purple-100">
            <Icon icon="mdi:robot" className="size-5 text-purple-600" />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium text-gray-900">{agentName}</span>
          <div className="flex items-center gap-1.5 mt-2 py-1 px-2 -ml-2 rounded-md">
            <Icon icon="mdi:lightbulb-outline" className="h-3.5 w-3.5 text-amber-500 animate-pulse" />
            <span className="text-xs text-gray-500">{t('ai.chatMessage.thinking')}</span>
          </div>
        </div>
      </div>
    );
  }

  // Build tool steps for ThinkingTimeline from saved tool_calls
  // Backend ToolCallInfo uses tool_name/arguments; SSE uses tool/args
  const SKILL_TOOLS = new Set(['list_skills', 'load_skill', 'read_skill_resource']);
  const visibleToolCalls = (message.tool_calls || []).filter((tc) => {
    const toolName = tc.tool_name || tc.name || tc.tool;
    return toolName && toolName !== 'final_result';
  });
  const toolSteps = visibleToolCalls.length > 0
    ? visibleToolCalls.map((tc) => {
        const toolName = tc.tool_name || tc.name || tc.tool;
        const args = tc.arguments || tc.args || {};
        const isSkillTool = SKILL_TOOLS.has(toolName);
        return {
          tool: toolName,
          query:
            args.query ||
            args.url ||
            args.skill_name ||
            args.resource_name ||
            args.document_uuid ||
            null,
          detail:
            args.instructions ||
            (isSkillTool ? null : tc.result_summary || null),
          status: 'completed',
        };
      })
    : null;

  return (
    <div className={`flex gap-3 py-4 ${isUser ? 'flex-row-reverse' : ''}`}>
      {/* Avatar */}
      <div className="flex-shrink-0">
        {isUser ? (
          <div className="flex items-center justify-center size-8 rounded-full bg-blue-100">
            <Icon icon="mdi:account" className="size-5 text-blue-600" />
          </div>
        ) : (
          <div className="flex items-center justify-center size-8 rounded-full bg-purple-100">
            <Icon icon="mdi:robot" className="size-5 text-purple-600" />
          </div>
        )}
      </div>

      {/* Message content */}
      <div className={`flex-1 min-w-0 ${isUser ? 'text-right' : ''}`}>
        <div className={`flex items-center gap-2 ${isUser ? 'justify-end' : ''}`}>
          <span className="text-sm font-medium text-gray-900">
            {isUser ? t('ai.chatMessage.you') : agentName}
          </span>
          {message.created_at && (
            <span className="text-xs text-gray-500">
              {formatTime(message.created_at, i18n.resolvedLanguage)}
            </span>
          )}
        </div>

        {/* Tool calls as ThinkingTimeline (assistant messages only) */}
        {!isUser && toolSteps && (
          <div className="mt-2">
            <ThinkingTimeline
              steps={toolSteps}
              isStreaming={false}
              isCollapsible={true}
            />
          </div>
        )}

        {/* Questions asked by the agent (shown as read-only in history) */}
        {!isUser && message.pending_questions && message.pending_questions.length > 0 && (
          <div className="mt-3 space-y-2">
            {message.pending_questions.map((q, index) => (
              <UserQuestionReadOnly key={index} question={q} />
            ))}
          </div>
        )}
        {!isUser && message.answered_questions && message.answered_questions.length > 0 && (
          <div className="mt-3 space-y-1">
            {message.answered_questions.map((q, index) => (
              <div key={index} className="flex gap-2 text-xs text-gray-400">
                <Icon icon="mdi:check-circle-outline" className="size-4 mt-0.5 flex-shrink-0" />
                <span>
                  {q.question_text}{' '}
                  <span className="text-gray-500">
                    {q.selected_options?.length
                      ? q.selected_options.map((o) => o.label).join(', ')
                      : q.other_text}
                  </span>
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Message text */}
        {isUser ? (
          <div className="mt-1 inline-block bg-blue-600 text-white rounded-2xl rounded-tr-md px-4 py-2 text-sm leading-relaxed">
            {message.content}
          </div>
        ) : (
          <MarkdownViewer
            content={message.content || ''}
            generatedFiles={message.generated_files}
            className="text-gray-700 prose-p:my-1 prose-headings:mt-3 prose-headings:mb-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0 mt-1"
            components={{
              p: ({ children }) => (
                <p>{processInlineCitations(children, message.citations, handleCitationClick)}</p>
              ),
              li: ({ children }) => (
                <li>{processInlineCitations(children, message.citations, handleCitationClick)}</li>
              ),
              a: ({ href, children, ...props }) => {
                if (href?.startsWith('upload://')) {
                  const label = Array.isArray(children)
                    ? children.map((c) => (typeof c === 'string' ? c : '')).join('')
                    : (typeof children === 'string' ? children : '');
                  return (
                    <UploadLinkWidget
                      href={href}
                      label={label}
                      onUploadComplete={onUploadComplete}
                    />
                  );
                }
                return <a href={href} {...props}>{children}</a>;
              },
            }}
          />
        )}

        {/* Citations (assistant messages only) — deduplicated by source */}
        {!isUser && message.citations?.length > 0 && (() => {
          const seen = new Set();
          const unique = message.citations.filter((c) => {
            const key = getCitationUrl(c) || c.document_uuid || c.document_name;
            if (!key || seen.has(key)) return false;
            seen.add(key);
            return true;
          });
          return (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-2">
              <Icon icon="mdi:format-quote-close" className="size-4" />
              <span>{t('ai.chatMessage.sources', { total: unique.length })}</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {unique.map((citation, index) => {
                const webUrl = getCitationUrl(citation);
                const href = webUrl || buildViewerHref(projectUuid, citation);
                return (
                  <a
                    key={citation.document_uuid || citation.chunk_id || index}
                    href={href || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded cursor-pointer transition-colors ${
                      webUrl
                        ? 'text-blue-600 bg-blue-50 hover:bg-blue-100'
                        : 'text-gray-600 bg-gray-100 hover:bg-gray-200'
                    }`}
                    title={citation.quoted_text}
                  >
                    <span className="font-medium">[{index + 1}]</span>
                    <Icon icon="mdi:open-in-new" className="size-3 shrink-0" />
                    <span className="truncate max-w-48">{formatCitationName(citation, t)}</span>
                  </a>
                );
              })}
            </div>
          </div>
          );
        })()}
      </div>
    </div>
  );
}

ChatMessage.propTypes = {
  message: PropTypes.shape({
    uuid: PropTypes.string,
    role: PropTypes.oneOf(['user', 'assistant', 'system']).isRequired,
    content: PropTypes.string,
    created_at: PropTypes.string,
    project_uuid: PropTypes.string,
    citations: PropTypes.arrayOf(
      PropTypes.shape({
        document_uuid: PropTypes.string,
        document_name: PropTypes.string,
        chunk_id: PropTypes.string,
        quoted_text: PropTypes.string,
        page_number: PropTypes.number,
        url: PropTypes.string,
        confidence: PropTypes.number,
      }),
    ),
    tool_calls: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        args: PropTypes.object,
        result: PropTypes.any,
      }),
    ),
    pending_questions: PropTypes.arrayOf(
      PropTypes.shape({
        question_text: PropTypes.string.isRequired,
        options: PropTypes.arrayOf(
          PropTypes.shape({
            label: PropTypes.string.isRequired,
            description: PropTypes.string,
          }),
        ),
        multi_select: PropTypes.bool,
      }),
    ),
    isLoading: PropTypes.bool,
    isOptimistic: PropTypes.bool,
  }).isRequired,
  agentName: PropTypes.string,
  onUploadComplete: PropTypes.func,
};
